import { CurrencyPipe, DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import localeEsCo from '@angular/common/locales/es-CO';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';
import { TranslocoRootModule } from '@app/transloco-root.module';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AppLoadService } from '@core/services/app-load.service';
import { appReducers } from '@core/store/app.reducer';
import { EffectsArray } from '@core/store/effects/index';
import { environment } from '@env';
import { provideHttpCache, withHttpCacheInterceptor } from '@ngneat/cashew';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgxPermissionsModule } from 'ngx-permissions';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogConfig, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';

import { routes } from './app.routing';
import { HttpRequestInterceptor } from './core/interceptors/http.interceptor';
import { HttpErrorInterceptor } from './core/interceptors/http-error-interceptor';

registerLocaleData(localeEsCo, 'es-CO');

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: environment.authority,
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: environment.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        // loggerCallback, // activate the MSAL debug log
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function init_app(appLoadService: AppLoadService, msalInstance: IPublicClientApplication) {
  return async () => {
    await msalInstance.initialize();
    return appLoadService.initializeApp();
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  /**
   * Important this scope generate the correct authorization Bearer token
   */
  const apiScopes = ['api://' + environment.clientId + '/Validation'];
  protectedResourceMap.set(environment.api_root, apiScopes);
  protectedResourceMap.set(environment.api_root_laas, apiScopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/login-failed',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withPreloading(PreloadAllModules)),
    importProvidersFrom(
      BlockUIModule,
      BrowserModule,
      BrowserAnimationsModule,
      StoreModule.forRoot(appReducers),
      EffectsModule.forRoot(EffectsArray),
      StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production, connectInZone: true }),
      StoreRouterConnectingModule.forRoot(),
      NgxPermissionsModule.forRoot(),
      ToastModule,
      DynamicDialogModule,
      DialogModule,
      TranslocoRootModule
    ),
    { provide: LOCALE_ID, useValue: 'es-CO' },
    AppLoadService,
    { provide: APP_INITIALIZER, useFactory: init_app, deps: [AppLoadService, MSAL_INSTANCE], multi: true },
    DialogService,
    MessageService,
    ConfirmationService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    CurrencyPipe,
    DynamicDialogRef,
    DynamicDialogConfig,
    DatePipe,
    DecimalPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideHttpClient(
      withInterceptors([withHttpCacheInterceptor(), HttpRequestInterceptor, HttpErrorInterceptor]),
      withInterceptorsFromDi()
    ),
    provideHttpCache(),
  ],
};
